import React from "react";
import { useNavigate } from "react-router-dom";
import MUIDataTable, { FilterType, Responsive, SelectableRows } from "mui-datatables";
import { ThemeProvider } from "@mui/material/styles";
import theme from "../../theme";
import { AlertColor, Box, Typography, Button, Collapse, TableRow, TableCell, Tooltip } from "@mui/material";

import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import EditRoundedIcon from "@mui/icons-material/EditRounded";

import LiveTvIcon from "@mui/icons-material/LiveTv";

import "./index.css";
import config, { ApiCompanyData, getConfigForRegion, Regions } from "../../config";
import { SyntheticEvent, useState } from "react";
import ButtonWithDialog from "../DialogButton";
import CustomSnackbar from "../../components/CustomSnackbar";
import { useAuth } from "../../context/AuthContext";
import CompanyLiveViewSessions from "./CompanyLiveViewSessions";
import axios from "axios";

const { ID_CREATE_NEW } = config.app;

interface CompaniesListProps {
    companiesValid: boolean;
    companies?: ApiCompanyData[];
}

export default function CompaniesList(props: CompaniesListProps) {
    const { companiesValid, companies } = props;
    const navigate = useNavigate();
    const data = (companiesValid && companies) || [];

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");
    const [isLoading, setIsLoading] = useState(false);
    const [expandedCompanyId, setExpandedCompanyId] = useState<number | null>(null);
    const [expandedCompanyRegion, setExpandedCompanyRegion] = useState<string | null>(null);

    const { refreshCompanies, headersWithAuth } = useAuth();

    const handleSnackbarClose = (event: SyntheticEvent<any, Event> | Event, reason?: string) => {
        if (reason === "clickaway") return;
        setOpenSnackbar(false);
    };

    const toggleLiveViewSessions = (companyId: number, region: string) => {
        if (expandedCompanyId === companyId && expandedCompanyRegion === region) {
            setExpandedCompanyId(null);
            setExpandedCompanyRegion(null);
        } else {
            setExpandedCompanyId(companyId);
            setExpandedCompanyRegion(region);
        }
    };

    const handleDelete = async (id: number, region: string) => {
        try {
            setIsLoading(true);
            setOpenSnackbar(true);
            console.log(`Deleting company: ${id} from region ${region} ...`);
            setSnackbarMessage(`Started Deleting the company: ${id} ...`);
            setSnackbarSeverity("success");

            // Find the matching region configuration
            const selectedRegion = Object.values(Regions).find((r) => r.displayName === region);
            if (!selectedRegion) throw new Error("Invalid region");

            const regionConfig = getConfigForRegion(selectedRegion);

            const response = await axios.post(regionConfig.api.DELETE_COMPANY, { id: id }, headersWithAuth);

            if (response.status === 200) {
                setSnackbarMessage("Company deleted successfully!");
                setSnackbarSeverity("success");
                refreshCompanies();
            } else if (response.status === 400) {
                console.log(response);
                console.log(`Deleted ${regionConfig.api.API_FAIL_ERROR}${response.status}`);
                setSnackbarMessage(response.data.message);
                setSnackbarSeverity("error");
            } else {
                console.log(`Deleted ${regionConfig.api.API_FAIL_ERROR}${response.status}`);
                setSnackbarMessage("Failed to delete company.");
                setSnackbarSeverity("error");
            }
        } catch (error: any) {
            console.log(error.response?.data?.message);
            if (error.response?.data?.message === "Cannot delete company as it either has users or systems.") {
                setSnackbarMessage(`Error: ${error.response.data.message}`);
                setSnackbarSeverity("error");
            } else {
                setSnackbarMessage(`Error: ${error.message || "Failed to delete company"}`);
                setSnackbarSeverity("error");
            }
        } finally {
            setIsLoading(false);
            setOpenSnackbar(true);
        }
    };

    const customRowRender = (data: any[], dataIndex: number) => {
        const company = companies?.[dataIndex];
        if (!company) return null;

        const isExpanded = expandedCompanyId === company.id && expandedCompanyRegion === company.region;

        return (
            <React.Fragment key={`row-${company.id}`}>
                <TableRow hover>
                    {data.map((cell, cellIndex) => (
                        <TableCell key={cellIndex}>{cell}</TableCell>
                    ))}
                </TableRow>

                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0, border: 0 }} colSpan={8}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2, marginBottom: 4 }}>
                                {/* <Typography variant="h6" gutterBottom component="div">
                                    Live View Sessions
                                </Typography> */}
                                <CompanyLiveViewSessions companyId={company.id} region={company.region} />
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            </React.Fragment>
        );
    };

    const buttonViewOptions = {
        sort: false,
        filter: false,
        viewColumns: false,
        customBodyRender: (value: any, tableMeta: { rowData: any[] }) => {
            const region = tableMeta.rowData[4]; // Region column position
            return (
                <Tooltip title="Edit">
                    <IconButton
                        onClick={() => navigate(`/company/${value}?region=${encodeURIComponent(region)}`)}
                        color="primary"
                        size="small"
                    >
                        <EditRoundedIcon />
                    </IconButton>
                </Tooltip>
            );
        }
    };

    const deleteButton = {
        sort: false,
        filter: false,
        viewColumns: false,
        customBodyRender: (companyId: number, tableMeta: { rowData: any[] }) => {
            const region = tableMeta.rowData[4];
            const companyToDelete = data.find((company) => company.id === companyId && company.region === region);

            if (!companyToDelete) {
                return null;
            }

            return (
                <ButtonWithDialog onDelete={() => handleDelete(companyToDelete.id, region)} typeOfOperation="Delete" />
            );
        }
    };

    const liveViewButton = {
        sort: false,
        filter: false,
        viewColumns: false,
        customBodyRender: (companyId: number, tableMeta: { rowData: any[] }) => {
            const region = tableMeta.rowData[4]; // Region column position
            const isExpanded = expandedCompanyId === companyId && expandedCompanyRegion === region;

            return (
                <Tooltip title={isExpanded ? "Hide Live View sessions" : "View Live View sessions"}>
                    <IconButton
                        aria-label={isExpanded ? "collapse Live View sessions" : "expand Live View sessions"}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleLiveViewSessions(companyId, region);
                        }}
                        color="primary"
                        data-cy={`liveview-toggle-${companyId}`}
                    >
                        <LiveTvIcon />
                    </IconButton>
                </Tooltip>
            );
        }
    };

    const newCompanyTitle = (
        <Button
            data-cy="create-company-button"
            onClick={() => navigate(`/company/${ID_CREATE_NEW}`)}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
        >
            Create Company
        </Button>
    );

    const COLUMNS = [
        { label: " ", name: "id", options: buttonViewOptions },
        { label: " ", name: "id", options: deleteButton },
        { label: " ", name: "id", options: liveViewButton },
        { label: "Name", name: "name", options: {} },
        { label: "Region", name: "region", options: {} },
        { label: "Address 1", name: "address1", options: {} },
        { label: "Address 2", name: "address2", options: {} },
        { label: "City", name: "city", options: {} }
    ];

    const OPTIONS = {
        download: false,
        filter: true,
        filterType: "dropdown" as FilterType,
        pagination: false,
        print: false,
        responsive: "vertical" as Responsive,
        search: true,
        selectableRows: "none" as SelectableRows,
        selectableRowsHideCheckboxes: true,
        storageKey: "companies",
        textLabels: {
            body: {
                noMatch: companiesValid ? "No companies available." : "Loading ..."
            }
        },
        customRowRender: customRowRender
    };

    const getMuiTheme = () => {
        const baseTheme = theme;
        return {
            ...baseTheme,
            components: {
                ...baseTheme.components,
                MUIDataTableHeadCell: {
                    styleOverrides: {
                        contentWrapper: {
                            justifyContent: "left"
                        }
                    }
                },
                MuiTableCell: {
                    styleOverrides: {
                        root: {
                            padding: "16px 8px"
                        }
                    }
                }
            }
        };
    };

    return (
        <Box className="companies-table">
            <ThemeProvider theme={getMuiTheme()}>
                <MUIDataTable title={newCompanyTitle} data={data} columns={COLUMNS} options={OPTIONS} />
            </ThemeProvider>

            <CustomSnackbar
                open={openSnackbar}
                handleClose={handleSnackbarClose}
                message={snackbarMessage}
                severity={snackbarSeverity}
                autoHideDuration={30000}
            />
        </Box>
    );
}
